import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import SignInIcon from '@mui/icons-material/Lock';
import HomeIcon from '@mui/icons-material/Home';
import VerificationIcon from '@mui/icons-material/VerifiedUser';
import SchoolIcon from '@mui/icons-material/School';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import InfoIcon from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography'; 
import ContactUsFloatingButton from './ContactusFloating';

function AppAppBar() {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const logoLeft = require('./images/logo.png'); 
  const logoRight = require('./images/pngwing.com.png'); 

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const handleNavigate = (path) => {
    setOpen(false);
    setLoading(true);
    setTimeout(() => {
      window.scrollTo(0, 0);
      navigate(path);
      setLoading(false);
    }, 100);
  };

  const getButtonStyle = (path) => {
    const isActive = location.pathname === path;
    return {
      textTransform: 'none',
      fontSize: '1rem',
      fontWeight: 500,
      color: '#FFFFFF',
      backgroundColor: isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
      borderRadius: '4px',
      px: 3, 
      mx: 1, 
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateY(-2px)',
        transition: 'all 0.2s'
      },
    };
  };

  const iconStyle = { color: '#FFFFFF', fontSize: '1.2rem' };
  const drawerIconStyle = { color: '#0d2e69', marginRight: '10px' };

  return (
    <div>
      {/* ===================================================================
          ROW 1: THE HEADER BANNER (Logos + Text)
          =================================================================== */}
      <Box sx={{ backgroundColor: 'white', width: '100%', borderBottom: '1px solid #e0e0e0', py: 1 }}>
        {/* Removed 'Container' here to use full width on mobile if needed, 
            but kept max-width logic via Box padding */}
        <Box sx={{ maxWidth: '100%', px: { xs: 1, md: 4 }, mx: 'auto' }}> 
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              // CRITICAL FIX: Always 'row', never 'column'
              flexDirection: 'row', 
              gap: { xs: 1, md: 2 } 
            }}
          >
            
            {/* --- LEFT: GITI LOGO --- */}
            <Box 
              component="img" 
              src={logoLeft} 
              alt="GITI Logo" 
              sx={{ 
                // Scaled down significantly for mobile to fit in the row
                height: { xs: '50px', sm: '80px', md: '130px' }, 
                width: 'auto',
                cursor: 'pointer',
                flexShrink: 0 // Prevents logo from getting squashed
              }}
              onClick={() => handleNavigate('/')}
            />

            {/* --- CENTER: INSTITUTE TEXT --- */}
            <Box sx={{ textAlign: 'center', flexGrow: 1, px: 0.5 }}>
              
              {/* English Name */}
              <Typography 
                variant="h1" 
                sx={{ 
                  // Tiny font on mobile (xs) so it fits on one line
                  fontSize: { xs: '0.7rem', sm: '1.4rem', md: '2.2rem' }, 
                  fontWeight: '800', 
                  color: '#0d2e69', 
                  letterSpacing: { xs: '0px', md: '0.5px' },
                  lineHeight: 1.1,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  textTransform: 'uppercase'
                }}
              >
                GENIUS INDUSTRIAL TRAINING INSTITUTE
              </Typography>
              
              {/* Hindi Name */}
              <Typography 
                variant="h2" 
                sx={{ 
                  fontSize: { xs: '0.6rem', sm: '1.1rem', md: '1.6rem' }, 
                  fontWeight: '600', 
                  color: '#0d2e69', 
                  mt: 0.2,
                  fontFamily: '"Noto Sans Devanagari", sans-serif' 
                }}
              >
                जीनियस औद्योगिक प्रशिक्षण संस्थान
              </Typography>

              {/* Recognition Text */}
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontSize: { xs: '0.5rem', sm: '0.8rem', md: '1rem' }, 
                  fontWeight: 'bold', 
                  color: '#333',
                  mt: 0.2,
                  textTransform: 'uppercase'
                }}
              >
                RECOGNISED BY GOVERNMENT OF INDIA
              </Typography>

              {/* Registration Details - Hidden on very small screens if needed, or made tiny */}
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  // Very small font on mobile to prevent overflow
                  fontSize: { xs: '0.35rem', sm: '0.5rem', md: '0.75rem' }, 
                  color: '#555',
                  mt: 0.2,
                  fontWeight: '500',
                  lineHeight: 1.1
                }}
              >
                (REGISTERED UNDER PUBLIC TRUST -033576/196 & MSME GOVT. REG. NO. UDYAM-CG-14-0116969)
                <br />
                (AN ISO 9001:2015 CERTIFIED INSTITUTE)
              </Typography>
            </Box>

            {/* --- RIGHT: MSME LOGO --- */}
            <Box 
              component="img" 
              src={logoRight} 
              alt="MSME Logo" 
              sx={{ 
                // Scaled down significantly for mobile
                height: { xs: '35px', sm: '60px', md: '110px' }, 
                width: 'auto',
                flexShrink: 0 // Prevents logo from getting squashed
              }}
            />

          </Box>
        </Box>
      </Box>

      {/* ===================================================================
          ROW 2: THE NAVIGATION BAR (Sticky)
          =================================================================== */}
      <AppBar 
        position="sticky" 
        sx={{ 
            backgroundColor: '#0d2e69', 
            boxShadow: 4,
            top: 0,
            zIndex: 1100
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: '56px', md: '64px' } }}>
            
            {/* Desktop Menu */}
            <Box 
              sx={{ 
                flexGrow: 1, 
                display: { xs: 'none', md: 'flex' }, 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}
            >
              <Button onClick={() => handleNavigate('/')} sx={getButtonStyle('/')}>
                <HomeIcon sx={{ ...iconStyle, mr: 1 }} />
                Home
              </Button>
              <Button onClick={() => handleNavigate('/verification')} sx={getButtonStyle('/verification')}>
                <VerificationIcon sx={{ ...iconStyle, mr: 1 }} />
                Verification
              </Button>
              <Button onClick={() => handleNavigate('/enroll')} sx={getButtonStyle('/enroll')}>
                <CastForEducationIcon sx={{ ...iconStyle, mr: 1 }} />
                Enroll
              </Button>
              <Button onClick={() => handleNavigate('/courses')} sx={getButtonStyle('/courses')}>
                <SchoolIcon sx={{ ...iconStyle, mr: 1 }} />
                Courses
              </Button>
              <Button onClick={() => handleNavigate('/contactus')} sx={getButtonStyle('/contactus')}>
                <ContactMailIcon sx={{ ...iconStyle, mr: 1 }} />
                Contact Us
              </Button>
              <Button onClick={() => handleNavigate('/about')} sx={getButtonStyle('/about')}>
                <InfoIcon sx={{ ...iconStyle, mr: 1 }} />
                About
              </Button>
            </Box>

            {/* Mobile Menu Icon */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
               <Button
                  size="large"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                  sx={{ color: 'white', textTransform: 'none', fontSize: '1.1rem' }}
                  startIcon={<MenuIcon />}
               >
                  Menu
               </Button>
            </Box>

            {/* Sign In Button */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', position: 'absolute', right: 0 }}>
              <Button
                variant="outlined"
                onClick={() => handleNavigate('/adminLogin')}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  color: '#FFFFFF',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: '#FFFFFF',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <SignInIcon sx={{ ...iconStyle, mr: 1, fontSize: '1rem' }} />
                Sign In
              </Button>
            </Box>

          </Toolbar>
        </Container>
      </AppBar>

      {/* --- MOBILE DRAWER --- */}
      <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            width: 260,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <img src={logoLeft} alt="Menu Logo" style={{ width: '80px', height: 'auto' }} />
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ flexGrow: 1 }}>
            <MenuItem onClick={() => handleNavigate('/')}>
              <HomeIcon sx={drawerIconStyle} /> Home
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/verification')}>
              <VerificationIcon sx={drawerIconStyle} /> Verification
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/enroll')}>
              <CastForEducationIcon sx={drawerIconStyle} /> Enroll
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/courses')}>
              <SchoolIcon sx={drawerIconStyle} /> Courses
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/contactus')}>
              <ContactMailIcon sx={drawerIconStyle} /> Contact Us
            </MenuItem>
            <MenuItem onClick={() => handleNavigate('/about')}>
              <InfoIcon sx={drawerIconStyle} /> About
            </MenuItem>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <MenuItem>
            <Button
              variant="contained"
              onClick={() => handleNavigate('/adminLogin')}
              sx={{ width: '100%', bgcolor: '#0d2e69' }}
            >
              <SignInIcon sx={{ mr: 1 }} />
              Sign In
            </Button>
          </MenuItem>
        </Box>
      </Drawer>

      {/* Loading Spinner */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <ContactUsFloatingButton />
    </div>
  );
}

export default AppAppBar;